/* eslint-disable @typescript-eslint/no-unused-vars */
import { OfferType, OfferTypeWithIds } from './types';

/**
 * Counts eligibility criteria for a promoter offer and returns formatted text
 */
export function getEligibilityText(
  eligibilityCriteria: { selector: string; ids: string[] }[] = []
) {
  const selectorCounts = ['users', 'organizations', 'associations'].reduce(
    (acc, selector) => {
      const count = eligibilityCriteria
        .filter((criteria) => criteria.selector === selector)
        .reduce((sum, criteria) => sum + criteria.ids.length, 0);
      if (count > 0) {
        acc[selector] = count;
      }
      return acc;
    },
    {} as Record<string, number>
  );

  return Object.entries(selectorCounts)
    .map(([selector, count]) => `${count} ${selector}`)
    .join(', ');
}

/**
 * Deep comparison helper function to check if two values are equal
 */
export function isEqual(a: any, b: any): boolean {
  if (a === b) {
    return true;
  }

  if (a == null || b == null) {
    return a === b;
  }

  if (typeof a !== typeof b) {
    return false;
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((item, index) => isEqual(item, b[index]));
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) {
      return false;
    }

    return keysA.every((key) => keysB.includes(key) && isEqual(a[key], b[key]));
  }

  return false;
}

/**
 * Returns the changed offer object with the difference between the existing offer and the new offer
 */
export function getChangedOffer(
  existingOffer: OfferTypeWithIds,
  newOffer: OfferTypeWithIds
): Partial<OfferTypeWithIds> {
  const removeIds = (offer: OfferTypeWithIds) => ({
    ...offer,
    eligibilityCriteria: offer.eligibilityCriteria.map((criteria) => ({
      mustHaveTicket: criteria.mustHaveTicket,
      selector: criteria.selector,
      ids: criteria.ids || []
    }))
  });

  const cleanedExistingOffer = removeIds(existingOffer);
  const cleanedNewOffer = removeIds(newOffer);

  const changes: Partial<OfferTypeWithIds> = {};

  // Check each top-level property
  const keys = Object.keys(cleanedNewOffer) as (keyof OfferTypeWithIds)[];

  keys.forEach((key) => {
    const existingValue = cleanedExistingOffer[key];
    const newValue = cleanedNewOffer[key];

    // If values are not equal, add to changes
    if (!isEqual(existingValue, newValue)) {
      (changes as any)[key] = newOffer[key];
    }
  });

  return changes;
}

/**
 * Returns the offer object with correct type
 */
export const getUpdatedOfferWithIds = (
  existingOffer: OfferType
): Omit<OfferTypeWithIds, 'contracts'> => {
  const { contracts, ...offerWithoutContracts } = existingOffer;
  const {
    minSalesThresholdEnabled,
    minSalesThreshold,
    tiers,
    ...saleConditions
  } = offerWithoutContracts.rewards.kickbackRewards.saleConditions;
  const offer = {
    ...offerWithoutContracts,
    rewards: {
      kickbackRewards: [
        {
          ...offerWithoutContracts.rewards.kickbackRewards,
          saleConditions: {
            ...saleConditions,
            tierIds: tiers?.map((tier) => tier.id) || [],
            minSalesThreshold: minSalesThresholdEnabled
              ? minSalesThreshold
              : undefined
          }
        }
      ]
    },
    eligibilityCriteria: offerWithoutContracts.eligibilityCriteria.map(
      (eligibility) => ({
        mustHaveTicket: eligibility.mustHaveTicket,
        selector: eligibility.selector,
        ids: eligibility.items?.map((item) => item.id) || []
      })
    )
  };

  return offer;
};
